import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Container, Typography, Stack, Paper, Grid, Chip, Avatar, TextField, Link as MuiLink, Fade, MenuItem, Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckIcon from '@mui/icons-material/Check';
import { useAuth } from '../context/AuthContext';
import emailjs from '@emailjs/browser';

const Landing: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Tabs data and state (must be top-level, not inside callbacks)
  const tabs = [
    { key: 'hr', label: 'HR and People', color: '#3d2a20' },
    { key: 'csuite', label: 'C-Suite', color: '#d8c9bf' },
    { key: 'comms', label: 'Comms', color: '#d8c9bf' },
    { key: 'product', label: 'Product & Tech', color: '#d8c9bf' },
    { key: 'hospitality', label: 'Hospitality', color: '#ffe6c8' },
    { key: 'charities', label: 'Charities', color: '#e6f3ff' },
  ] as const;
  type TabKey = typeof tabs[number]['key'];
  const [active, setActive] = useState<TabKey>('hr');
  // Simple carousel items for stats/claims
  const carouselItems = useMemo(
    () => [
      {
        headline: 'Only 1 in 10 UK employees actually feel excited to go to work.',
        subtext: 'Gallup report',
      },
      {
        headline: 'Highly engaged teams see lower absenteeism and higher productivity',
        subtext: 'Gallup, 2023',
      },
      {
        headline: 'Strong cultures retain top talent and accelerate outcomes',
        subtext: 'Deloitte research',
      },
    ],
    []
  );
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [openJoin, setOpenJoin] = useState(false);
  // Helper to scroll to the join form (used by multiple CTAs)
  const scrollToJoin = () => {
    const el = document.getElementById('join');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate('/#join');
    }
  };
  // Join form submit
  const handleJoinSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      firstName: fd.get('firstName') as string,
      lastName: fd.get('lastName') as string,
      email: fd.get('email') as string,
      jobTitle: fd.get('jobTitle') as string,
      companyName: fd.get('companyName') as string,
      teamSize: fd.get('teamSize') as string,
    };

    // EmailJS configuration via environment variables
    const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID as string;
    const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID as string;
    const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY as string;

    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      console.warn('EmailJS env vars missing - form data logged instead:', { SERVICE_ID, TEMPLATE_ID, PUBLIC_KEY });
      console.log('Form submission (EmailJS disabled):', payload);
      // Still show success to user for demo purposes
      setFormSubmitted(true);
      return;
    }

    // Prepare email template parameters
    const templateParams = {
      from_name: `${payload.firstName} ${payload.lastName}`,
      from_email: payload.email,
      company_name: payload.companyName,
      job_title: payload.jobTitle,
      team_size: payload.teamSize,
      to_name: 'SuperGauge Team',
      message: `
New lead from SuperGauge landing page:

Name: ${payload.firstName} ${payload.lastName}
Email: ${payload.email}
Company: ${payload.companyName}
Job Title: ${payload.jobTitle}
Team Size: ${payload.teamSize}

---
This lead was submitted from the SuperGauge landing page form.
      `.trim(),
    };

    try {
      // Initialize EmailJS (only needed once)
      if (!(window as any).__emailjs_inited) {
        emailjs.init(PUBLIC_KEY);
        (window as any).__emailjs_inited = true;
      }

      // Send email
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams
      );

      // Set form as submitted on success
      setFormSubmitted(true);

      console.log('Email sent successfully');
    } catch (error) {
      console.error('Failed to send email:', error);
      // You could show an error message to the user here
      alert('Failed to send email. Please try again or contact us directly.');
    }

    // Log for debugging
    console.log('Join submission', payload);
  };
  const content: Record<TabKey, { title: string; bullets: string[]; bg: string; image: string; imageAlt: string }> = {
    hr: {
      title: 'Carry out seamless engagement experiences',
      bullets: [
        'Avoid and overcome internal tech and people barriers',
        'Change the perception of employee engagement',
        'Guide and support your teams through difficult transitions',
        'Boost engagement',
      ],
      bg: '#e7bf9f',
      image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=1200&auto=format&fit=crop',
      imageAlt: 'HR and People leader',
    },
    csuite: {
      title: 'Lead with clarity and measurable outcomes',
      bullets: [
        'Track engagement and productivity across org units',
        'Identify risks early with sentiment trends',
        'Align initiatives to business outcomes',
      ],
      bg: '#cfe3ff',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?q=80&w=1200&auto=format&fit=crop',
      imageAlt: 'Executive team in boardroom discussion',
    },
    comms: {
      title: 'Communicate with resonance and reach',
      bullets: [
        'Test messaging and measure understanding',
        'Close the loop with data-backed updates',
        'Amplify trust through transparency',
      ],
      bg: '#ffd9ec',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=1200&auto=format&fit=crop',
      imageAlt: 'Team communications',
    },
    product: {
      title: 'Build better products with people insights',
      bullets: [
        'Prioritise roadmap with internal feedback signals',
        'Reduce friction in cross-functional collaboration',
        'Accelerate delivery with aligned teams',
      ],
      bg: '#d8ffe9',
      image: 'https://images.unsplash.com/photo-1552581234-26160f608093?q=80&w=1200&auto=format&fit=crop',
      imageAlt: 'Product and tech collaboration',
    },
    hospitality: {
      title: 'Delight guests with engaged front-line teams',
      bullets: [
        'Reduce staff turnover and improve guest experience',
        'Spot operational issues early from on-the-ground feedback',
        'Lift service quality with data-led action plans',
      ],
      bg: '#fff2e0',
      image: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=1200&auto=format&fit=crop',
      imageAlt: 'Hotel staff providing excellent guest service',
    },
    charities: {
      title: 'Support your mission by supporting your people',
      bullets: [
        'Understand volunteer and staff wellbeing and retention',
        'Align limited resources to the highest-impact initiatives',
        'Strengthen culture across distributed teams and sites',
      ],
      bg: '#eaf4ff',
      image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?q=80&w=1200&auto=format&fit=crop',
      imageAlt: 'Nonprofit team collaborating on community impact',
    },
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  // Initialize tab from hash and sync hash when tab changes
  useEffect(() => {
    const hashKey = (location.hash || '').replace('#', '') as TabKey;
    if (hashKey && (tabs as readonly any[]).some((t) => t.key === hashKey)) {
      setActive(hashKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Open join modal when URL hash is #join
  useEffect(() => {
    const hashKey = (location.hash || '').replace('#', '');
    if (hashKey === 'join') {
      setFormSubmitted(false);
      setOpenJoin(true);
    }
  }, [location.hash]);

  // Auto-advance carousel
  useEffect(() => {
    const id = setInterval(() => {
      setCarouselIndex((i) => (i + 1) % carouselItems.length);
    }, 7000);
    return () => clearInterval(id);
  }, [carouselItems.length]);

  return (
    <Box
      className="landing-page"
      sx={{
        bgcolor: '#F8F8FE',
        '& h1, & h2, & h3, & h4, & h5': { fontFamily: 'Manrope', fontWeight: 600 },
        '& h6': { fontFamily: 'Manrope', fontWeight: 600 },
      }}
    >
    

      {/* Hero */}
      <Box
        className="landing-hero"
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
          background: (t) => `linear-gradient(135deg, ${t.palette.grey[100]} 0%, ${t.palette.background.default} 100%)`,
        }}
      >
        {/* soft pattern */}
        <Box
          className="landing-hero__bg"
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `linear-gradient(rgba(255,255,255,0.65), rgba(255,255,255,0.65)), url(${process.env.PUBLIC_URL}/herobg.webp)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            pointerEvents: 'none',
          }}
        />
        <Container className="landing-hero__container" maxWidth="lg" sx={{ position: 'relative' }}>
          <Box className="landing-hero__content" sx={{ maxWidth: 900, mx: 'auto', textAlign: 'center' }}>
            <Typography className="landing-hero__kicker" variant="subtitle2" component="p" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
              Employee engagement made easy
            </Typography>
            <Typography className="landing-hero__headline" variant="h2" component="h1" sx={{ fontWeight: 800, letterSpacing: -0.5 }}>
              Be the company everyone wants to be part of
            </Typography>
            <Typography className="landing-hero__subtitle" variant="h6" color="text.secondary" sx={{ mt: 2 }}>
An easy-setup, low-effort employee engagement dashboard. Find out what your people think and help them thrive.            </Typography>
            <Stack className="landing-hero__ctas" direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mt: 4 }}>
              <Button
                className="landing-hero__cta landing-hero__cta--primary"
                variant="contained"
                onClick={() => navigate('/register')}
              >
                Try it free
              </Button>
            </Stack>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1.5, opacity: 0.7 }}
            >
              No credit card required
            </Typography>

            {/* Hero supporting image */}
            <Box className="landing-hero__mock" sx={{ mt: 6 }}>
              <Paper className="imgcontainer" elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <Box
                  component="img"
                  className="landing-hero__image"
                  src={`${process.env.PUBLIC_URL}/Dashboard%20_%20Home2.webp`}
                  alt="Manager dashboard"
                  sx={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </Paper>
            </Box>

      {/* Join modal */}
      <Dialog
        className="join-modal"
        open={openJoin}
        onClose={() => setOpenJoin(false)}
        maxWidth="lg"
        fullWidth
        aria-labelledby="join-dialog-title"
        PaperProps={{
          className: 'join-modal__paper',
          sx: {
            borderRadius: 4,
            overflow: 'auto',
            maxWidth: 1000,
            width: '100%',
            m: { xs: 1, sm: 2 },
            height: { xs: 'auto', sm: 'auto' },
            maxHeight: { xs: 'none', sm: '90vh' },
          },
        }}
      >
        <DialogContent className="join-modal__content" sx={{ p: { xs: 2, md: 4 }, position: 'relative', height: '100%', display: 'flex' }}>
          <IconButton
            aria-label="Close"
            className="join-modal__close-icon"
            onClick={() => setOpenJoin(false)}
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Grid className="join-modal__grid" container spacing={0} alignItems="center" sx={{ height: '100%', flex: 1 }}>
            <Grid
              className="join-modal__image-col"
              item
              xs={12}
              md={6}
              sx={{
                display: { xs: 'none', md: 'block' },
                backgroundImage: `url(${process.env.PUBLIC_URL}/ill_5.svg)`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                height: '100%',
              }}
            />
            <Grid className="join-modal__form-col" item xs={12} md={6} sx={{ pl: { md: 2 }, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              {!formSubmitted ? (
                <Box className="join-modal__form-wrap">
                  <Typography id="join-dialog-title" className="join-modal__title" variant="h4" sx={{ fontWeight: 800 }} gutterBottom>
                    Run a free survey with your team
                  </Typography>
                  <Typography className="join-modal__subtitle" variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Leave your details and we’ll get you set up.
                  </Typography>
                  <Box component="form" className="join-modal__form" onSubmit={handleJoinSubmit} noValidate>
                    <Grid className="join-modal__fields" container spacing={2}>
                      <Grid className="join-modal__field-col" item xs={12} sm={6}>
                        <TextField className="join-modal__field" name="firstName" label="First name" required fullWidth />
                      </Grid>
                      <Grid className="join-modal__field-col" item xs={12} sm={6}>
                        <TextField className="join-modal__field" name="lastName" label="Last name" required fullWidth />
                      </Grid>
                      <Grid className="join-modal__field-col" item xs={12}>
                        <TextField className="join-modal__field" type="email" name="email" label="Email address" required fullWidth />
                      </Grid>
                      <Grid className="join-modal__field-col" item xs={12}>
                        <TextField className="join-modal__field" name="companyName" label="Company" required fullWidth />
                      </Grid>
                      <Grid className="join-modal__field-col" item xs={12} sm={6}>
                        <TextField className="join-modal__field" name="jobTitle" label="Job Title" fullWidth />
                      </Grid>
                      <Grid className="join-modal__field-col" item xs={12} sm={6}>
                        <TextField className="join-modal__field" name="teamSize" label="Team size" select required fullWidth defaultValue="1-10">
                          <MenuItem value="1-10">1-10</MenuItem>
                          <MenuItem value="11-50">11-50</MenuItem>
                          <MenuItem value="51-100">51-100</MenuItem>
                          <MenuItem value="100+">100+</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid className="join-modal__actions-col" item xs={12}>
                        <Stack className="join-modal__actions" direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                          <Button className="join-modal__submit" type="submit" variant="contained" size="large">Get started</Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              ) : (
                <Box className="join-modal__thankyou" sx={{ textAlign: 'center', py: 4 }}>
                  <Typography className="join-modal__thankyou-icon" variant="h1" sx={{ fontSize: 64, color: 'primary.main', mb: 2 }}>✓</Typography>
                  <Typography className="join-modal__thankyou-title" variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
                    Thank you!
                  </Typography>
                  <Typography className="join-modal__thankyou-message" variant="body1" sx={{ mb: 2 }}>
                    We'll set up your free account and let you know when it's ready.
                  </Typography>
                
                  <Button className="join-modal__close" variant="contained" onClick={() => setOpenJoin(false)}>Close</Button>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
          </Box>
        </Container>
      </Box>

     

      {/* Benefits section */}
      <Box className="landing-benefits" sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="stretch" sx={{ mb: 4 }}>
            <Grid item xs={12} md={8}>
              <Typography className="landing-benefits__headline" variant="h4" sx={{ fontWeight: 800 }}>
                Get real, honest and actionable feedback from your people
              </Typography>
              <Typography className="landing-benefits__subtitle" variant="body1" color="text.secondary" sx={{ mt: 1.5 }}>
Whether you’re a small restaurant or big tech company — real feedback matters.

Our Cultural Diagnostic survey is a wellbeing and engagement check-up. It’s give your team a chance to share their thoughts and ideas for improving your culture.              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                textAlign: { xs: 'left', md: 'right' },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
              }}
            >
              <Button variant="contained" size="large" onClick={() => navigate('/register')} sx={{ alignSelf: { xs: 'flex-start', md: 'flex-end' } }}>
                Try it free
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {[{
              title: 'Set up a free cultural diagnostic survey within minutes',
            }, {
              title: 'View responses and engagement on your own dashboard',
            }, {
              title: 'Get insights into how to help your people thrive',
            }].map((card, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Paper className="landing-benefits__card" elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 800 }} gutterBottom>
                    {card.title}
                  </Typography>
                  <Box
                    component="img"
                    className="landing-benefits__visual"
                    src={`${process.env.PUBLIC_URL}/benefit_${idx + 1}.webp`}
                    alt={`Benefit ${idx + 1} illustration`}
                    sx={{ mt: 2, height: 220, width: '100%', borderRadius: 3, objectFit: 'cover', display: 'block' }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      

      {/* Companies/stat carousel */}
      <Box className="landing-carousel" sx={{ py: { xs: 10, md: 14 }, position: 'relative' }}>
        <Container maxWidth="md" sx={{ position: 'relative' }}>
          <Box className="landing-carousel__viewport" sx={{ position: 'relative', overflow: 'hidden' }}>
            <Box
              className="landing-carousel__track"
              sx={{ display: 'flex', transition: 'transform 600ms ease', transform: `translateX(-${carouselIndex * 100}%)` }}
            >
              {carouselItems.map((item, i) => (
                <Box key={i} className="landing-carousel__slide" sx={{ flex: '0 0 100%', px: { xs: 2, md: 4 } }}>
                  <Box className="landing-carousel__halo" sx={{ textAlign: 'center', px: { xs: 1, md: 4 } }}>
                    <Typography
                      className="landing-carousel__headline"
                      variant="h4"
                      align="center"
                      sx={{ fontWeight: 800, lineHeight: 1.2, fontSize: { xs: 24, md: 40 }, letterSpacing: -0.2, color: '#001a66' }}
                    >
                      {item.headline}
                    </Typography>
                    <Typography
                      className="landing-carousel__subtext"
                      variant="body2"
                      align="center"
                      sx={{ mt: 1.5, color: '#8a94b0' }}
                    >
                      {item.subtext}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          {/* dots */}
          <Stack className="landing-carousel__dots" direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
            {carouselItems.map((_, i) => (
              <Box
                className="landing-carousel__dot"
                key={i}
                onClick={() => setCarouselIndex(i)}
                sx={{
                  width: i === carouselIndex ? 10 : 8,
                  height: i === carouselIndex ? 10 : 8,
                  borderRadius: '50%',
                  bgcolor: i === carouselIndex ? 'primary.main' : 'grey.400',
                  opacity: i === carouselIndex ? 1 : 0.6,
                  cursor: 'pointer',
                }}
              />
            ))}
          </Stack>
        </Container>

        {/* Bottom illustrations */}
        <Box className="landing-carousel__illustrations" sx={{ position: 'relative', height: { xs: 220, md: 260 }, mt: { xs: 6, md: 8 } }}>
          <Box component="img" src={`${process.env.PUBLIC_URL}/ill_1.svg`} alt="Illustration left" className="landing-carousel__ill landing-carousel__ill--left" sx={{ position: 'absolute', left: { xs: -10, md: 40 }, bottom: 0, width: { xs: 120, md: 220 } }} />
          <Box component="img" src={`${process.env.PUBLIC_URL}/ill_2.svg`} alt="Illustration center" className="landing-carousel__ill landing-carousel__ill--center" sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', bottom: 0, width: { xs: 140, md: 260 } }} />
          <Box component="img" src={`${process.env.PUBLIC_URL}/ill_3.svg`} alt="Illustration right" className="landing-carousel__ill landing-carousel__ill--right" sx={{ position: 'absolute', right: { xs: -10, md: 40 }, bottom: 0, width: { xs: 120, md: 220 } }} />
        </Box>
      </Box>

      {/* Snapshot section */}
      <Box className="landing-snapshot" sx={{ py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography className="landing-snapshot__eyebrow" variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                Quick and easy to complete
              </Typography>
              <Typography className="landing-snapshot__headline" variant="h4" sx={{ fontWeight: 800, mt: 1 }}>
                Get a free snapshot of how your people feel
              </Typography>
              <Typography className="landing-snapshot__subtitle" variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Our Cultural Diagnostic Survey is designed by workplace wellbeing experts to measure 8 key characteristics of successful and engaged teams: Wellbeing, Trust, Resilience, Relationships, Culture, Collaboration, Autonomy and Transparency
              </Typography>
              <Button className="landing-snapshot__cta" variant="contained" size="large" sx={{ mt: 3 }} onClick={() => navigate('/register')}>
                Try it free
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                className="landing-snapshot__image"
                src={`${process.env.PUBLIC_URL}/iphoneshot.webp`}
                alt="Phone showing survey question UI"
                sx={{ width: '100%', height: 'auto', maxWidth: 520, display: 'block', mx: { xs: 'auto', md: 0 } }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

       {/* Role-based tabs section (below snapshot) */}
       <Box className="landing-performance" sx={{ py: 8, background: (t) => t.palette.mode === 'light' ? 'linear-gradient(180deg, #fff, #f7f4ff)' : 'transparent' }}>
        <Container className="landing-performance__container" maxWidth="lg">
          <Typography className="landing-performance__headline" align="center" variant="h4" sx={{ fontWeight: 800, mb: 2 }}>
            Happy teams are high performing teams
          </Typography>

          <Stack className="landing-performance__tabs" direction="row" spacing={1.5} justifyContent="center" sx={{ mb: 4, mt: 2, flexWrap: 'wrap' }}>
            {tabs.map((t) => (
              <Chip
                key={t.key}
                label={t.label}
                onClick={() => setActive(t.key)}
                className="landing-performance__tab"
                data-active={active === t.key}
                sx={{
                  borderRadius: 3,
                  px: 1,
                  color: active === t.key ? 'common.white' : 'text.primary',
                  bgcolor: active === t.key ? 'text.primary' : 'action.hover',
                }}
              />
            ))}
          </Stack>

          <Grid className="landing-performance__content" container spacing={6} alignItems="center">
            <Grid className="landing-performance__image-container" item xs={12} md={6}>
              <Paper className="landing-performance__image-wrapper" sx={{ borderRadius: 4, overflow: 'hidden' }} elevation={0}>
                <Box sx={{ position: 'relative', height: 450, bgcolor: content[active].bg }}>
                  <Fade in key={active} timeout={400}>
                    <Box
                      component="img"
                      src={content[active].image}
                      alt={content[active].imageAlt}
                      className="landing-performance__image"
                      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Fade>
                </Box>
              </Paper>
            </Grid>
            <Grid className="landing-performance__text-container" item xs={12} md={6}>
              <Fade in key={`text-${active}`} timeout={300}>
                <Box className="landing-performance__text-content">
                  <Typography className="landing-performance__title" variant="h5" sx={{ fontWeight: 800 }} gutterBottom>
                    {content[active].title}
                  </Typography>
                  <Stack className="landing-performance__benefits" spacing={1.5} sx={{ mt: 2 }}>
                    {content[active].bullets.map((b, i) => (
                      <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start" className="landing-performance__benefit">
                        <CheckIcon color="primary" sx={{ mt: '2px' }} />
                        <Typography className="landing-performance__benefit-text">{b}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      

      {/* Laptop Background Section */}
      <Box
        className="laptop-section"
        sx={{
          height: '500px',
          width: '100%',
          backgroundImage: `url(${process.env.PUBLIC_URL}/laptop2.webp)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          paddingTop: '100px',
        }}
      />

      {/* Newsletter + Footer combined */}
      <Box className="site-footer" sx={{ pt: 10, pb: 4, bgcolor: '#000430', color: '#ffffff' }}>
        
       

        {/* Middle nav columns */}
        <Container className="site-footer__links" maxWidth="lg" sx={{ mt: 8 }}>
          <Grid className="site-footer__grid" container spacing={6}>
            <Grid className="site-footer__brand" item xs={12} md={4}>
              <Box
                component="img"
                className="site-footer__logo"
                src={`${process.env.PUBLIC_URL}/sglogo.svg`}
                alt="SuperGauge"
                sx={{ height: 32, width: 'auto', display: 'block', mb: 1.5 }}
              />
              <Typography className="site-footer__branddesc" variant="body2" sx={{ mt: 1.5, color: 'rgba(255,255,255,0.8)' }}>
                Gather the insights you need to make people-driven, data-informed decisions about your company culture.
              </Typography>
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* <Stack className="site-footer__list" spacing={1}>
                <Typography className="site-footer__link" variant="body2">Platform</Typography>
                <Typography className="site-footer__link" variant="body2">Benefits</Typography>
                <Typography className="site-footer__link" variant="body2">Solutions</Typography>
              </Stack> */}
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* <Stack className="site-footer__list" spacing={1}>
                <Typography className="site-footer__link" variant="body2">About</Typography>
                <Typography className="site-footer__link" variant="body2">Company</Typography>
                <Typography className="site-footer__link" variant="body2">Contact</Typography>
              </Stack> */}
            </Grid>
            <Grid className="site-footer__col" item xs={12} md={2}>
              {/* <Stack className="site-footer__list" spacing={1}>
                <Typography className="site-footer__link" variant="body2">Pricing</Typography>
                <Typography className="site-footer__link" variant="body2">Log in</Typography>
                <Typography className="site-footer__link" variant="body2">Demo</Typography>
              </Stack> */}
            </Grid>
          </Grid>
        </Container>

        {/* Bottom bar */}
        <Container className="site-footer__bar" maxWidth="lg" sx={{ mt: 6 }}>
          <Box className="site-footer__bar-inner" sx={{ borderTop: '1px solid', borderColor: 'rgba(255,255,255,0.2)', pt: 2 }}>
            <Grid className="site-footer__bar-grid" container alignItems="center">
              <Grid className="site-footer__bar-left" item xs={12} md={6}>
                <Typography className="site-footer__copyright" variant="caption">© {new Date().getFullYear()} SuperGauge</Typography>
                
              </Grid>
              <Grid className="site-footer__bar-right" item xs={12} md={6}>
                <Box className="site-footer__contact" sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography className="site-footer__link" variant="caption">Contact</Typography>
                                  <Typography className="site-footer__link" variant="caption">Terms</Typography>
                  <Typography className="site-footer__link" variant="caption">Privacy policy</Typography>

                </Box>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Landing;
